/**
 * Live Update — bootstrap do @capgo/capacitor-updater no APK.
 *
 * Estratégia (revisada 2026-06-25):
 *
 * Histórico de tentativas que NÃO funcionaram:
 *   1. `autoUpdate: 'atBackground'` no plugin: só dispara em minimize real.
 *      Operadores em campo fecham por kill no recents — trigger nunca acontece.
 *   2. Check ativo no boot + `next({ id })` (commit 70fbb9d): bundle ficava
 *      enfileirado mas só aplicava no próximo background OU restart. Em sessões
 *      longas (operador abre app de manhã e mantém aberto o dia todo), o
 *      `next()` nunca chega a aplicar. Pior: silencioso — ninguém percebia.
 *
 * Estratégia atual (controle 100% manual + aplicação interativa):
 *   - Plugin com `autoUpdate: false` (no capacitor.config.ts) — sem race.
 *   - `notifyAppReady()` no boot pra plugin não reverter pro bundle nativo.
 *   - Boot check + periódico 15 min consultam o manifest no R2.
 *   - Quando há nova versão: BAIXA via `download()` e seta `pendingUpdate` no
 *     `liveUpdateStore`. NÃO chama `next()`. Aplicação é manual via banner.
 *   - Banner amarelo no topo (UpdateBanner.tsx) lê o store e oferece botão
 *     "Atualizar agora" → chama `applyPendingUpdate()` → `set({ id })` +
 *     `reload()`. Operador decide o momento; drafts ficam salvos no Dexie.
 *   - Tela /admin/live-update mostra log persistente pra debug em campo (tap
 *     longo no v0.X.Y do footer).
 *
 * No-op em PWA web (plugin não existe fora do WebView).
 */

import { CapacitorUpdater } from '@capgo/capacitor-updater'
import { isNative } from './runtime'
import { useLiveUpdateStore } from '@/features/liveUpdate/liveUpdateStore'

const PERIODIC_CHECK_MS = 15 * 60 * 1000

type Updater = typeof CapacitorUpdater

/**
 * Retorna o plugin Capgo OU null em PWA web. Static import — sem dynamic
 * chunk. (Tentamos dynamic import antes e o WebView Android trava
 * indefinidamente carregando o chunk separado via file://; o timeout de
 * 5s confirmou o trava em campo, log: "import plugin timeout 5000ms".)
 */
function getUpdater(): Updater | null {
  if (!isNative()) return null
  return CapacitorUpdater
}

/**
 * Promise.race com timeout. Plugin nativo Capgo pode travar await
 * indefinidamente em condições raras (rede offline + DNS lento, plugin não
 * registrado, etc.) — sem timeout, o bootstrap fica preso e nenhum log
 * aparece pro operador.
 */
function withTimeout<T>(label: string, ms: number, p: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`${label} timeout ${ms}ms`)), ms)
    p.then(
      (v) => {
        clearTimeout(t)
        resolve(v)
      },
      (e) => {
        clearTimeout(t)
        reject(e)
      },
    )
  })
}

function logInfo(msg: string): void {
  console.info(`[liveUpdate] ${msg}`)
  useLiveUpdateStore.getState().pushEvent('info', msg)
}

function logWarn(msg: string, err?: unknown): void {
  const full = err ? `${msg}: ${errString(err)}` : msg
  console.warn(`[liveUpdate] ${full}`)
  useLiveUpdateStore.getState().pushEvent('warn', full)
}

function errString(e: unknown): string {
  if (e instanceof Error) return e.message
  if (typeof e === 'string') return e
  try {
    return JSON.stringify(e)
  } catch {
    return String(e)
  }
}

let bootstrapped = false

export async function bootstrapLiveUpdate(): Promise<void> {
  if (bootstrapped) {
    logInfo('bootstrap já rodou — ignorando segunda chamada (StrictMode?)')
    return
  }
  bootstrapped = true

  // Loga ANTES de qualquer await pra confirmar pro operador, via tela de
  // diagnóstico, que a função foi chamada. Se zero eventos depois do boot,
  // é sinal de que App.tsx nem chegou a montar (problema mais grave).
  logInfo('bootstrap iniciado')

  const Updater = getUpdater()
  if (!Updater) {
    logInfo('plugin indisponível (PWA web)')
    return
  }
  logInfo('plugin carregado ✓')

  try {
    const ready = await withTimeout('notifyAppReady', 5000, Updater.notifyAppReady())
    useLiveUpdateStore.getState().setCurrent(ready.bundle.version)
    logInfo(`notifyAppReady ok, current=${ready.bundle.version}`)
  } catch (e) {
    logWarn('notifyAppReady falhou', e)
    // Mesmo se notifyAppReady falhar, segue pro check — o plugin pode estar
    // OK e só notify ter timing ruim.
  }

  void checkAndQueueUpdate(Updater).catch((e) => logWarn('check no boot falhou', e))

  setInterval(
    () => {
      void checkAndQueueUpdate(Updater).catch((e) =>
        logWarn('check periódico falhou', e),
      )
    },
    PERIODIC_CHECK_MS,
  )
}

/**
 * Consulta manifest, baixa o bundle se há versão nova, e enfileira pendingUpdate
 * no store. NÃO aplica — aplicação é via applyPendingUpdate() (banner ou tela admin).
 */
async function checkAndQueueUpdate(Updater: Updater): Promise<void> {
  const store = useLiveUpdateStore.getState()
  store.startCheck()

  let current: { bundle: { version: string } }
  try {
    current = await withTimeout('current', 5000, Updater.current())
    store.setCurrent(current.bundle.version)
  } catch (e) {
    store.finishCheck('error', errString(e))
    logWarn('current() falhou', e)
    return
  }

  let latest: Awaited<ReturnType<Updater['getLatest']>>
  try {
    latest = await withTimeout('getLatest', 15000, Updater.getLatest())
  } catch (e) {
    store.finishCheck('error', errString(e))
    logWarn('getLatest() falhou', e)
    return
  }

  const latestVersion = 'version' in latest ? latest.version : null
  logInfo(
    `check: current=${current.bundle.version} latest=${latestVersion ?? '(none)'} kind=${latest.kind ?? '(legacy)'}`,
  )

  if (latest.kind === 'up_to_date') {
    store.finishCheck('up_to_date')
    return
  }
  if (latest.kind === 'blocked') {
    store.finishCheck('error', 'update bloqueado pelo plugin')
    logWarn(`update bloqueado: ${JSON.stringify(latest)}`)
    return
  }
  if (!latest.url || !latestVersion) {
    store.finishCheck('up_to_date')
    logInfo('manifest sem url/version — nada a baixar.')
    return
  }
  if (latestVersion === current.bundle.version) {
    store.finishCheck('up_to_date')
    return
  }

  // Se já há um pendingUpdate da MESMA versão, não baixa de novo.
  const pending = store.pendingUpdate
  if (pending && pending.version === latestVersion) {
    store.finishCheck('newer')
    logInfo(`pendingUpdate já existe pra ${latestVersion} — aguardando aplicação.`)
    return
  }

  logInfo(`baixando bundle ${latestVersion}...`)
  try {
    const bundle = await Updater.download({
      url: latest.url,
      version: latestVersion,
      // O plugin compara com o SHA-256 em hex puro; tolera manifest antigo com prefixo "sha256-".
      checksum: latest.checksum ? latest.checksum.replace(/^sha256-/i, '') : undefined,
      sessionKey: latest.sessionKey ?? undefined,
    })
    store.setPending({ version: bundle.version, id: bundle.id })
    store.finishCheck('newer')
    logInfo(`bundle ${bundle.version} baixado (id=${bundle.id}). Aguardando aplicação manual.`)
  } catch (e) {
    store.finishCheck('error', errString(e))
    logWarn(`download falhou`, e)
  }
}

/**
 * Aplica o pendingUpdate IMEDIATAMENTE: `set({ id })` + `reload()` nativo do
 * plugin. Drafts do Dexie sobrevivem ao reload — operador retoma de onde
 * estava. Chamada pelo banner (UpdateBanner.tsx) e pela tela admin.
 *
 * IMPORTANTE: usa `Updater.reload()` (método do plugin) e NÃO
 * `window.location.reload()`. A diferença:
 *  - `Updater.reload()` reinicia o WebView CARREGANDO o bundle que `set()`
 *    marcou como ativo. É o método oficial.
 *  - `window.location.reload()` apenas recarrega a página DENTRO do mesmo
 *    bundle (o builtin se set() ainda não trocou). Esse era o bug: banner
 *    voltava porque o WebView nunca trocava de bundle.
 *
 * Limpa pendingUpdate ANTES do set pra o banner não voltar imediato se
 * algo der errado e o app voltar pro mesmo bundle.
 */
export async function applyPendingUpdate(): Promise<void> {
  const Updater = getUpdater()
  if (!Updater) return
  const pending = useLiveUpdateStore.getState().pendingUpdate
  if (!pending) {
    logWarn('applyPendingUpdate sem pendingUpdate — no-op.')
    return
  }
  logInfo(`aplicando bundle ${pending.version} (id=${pending.id})...`)
  // Limpa o pending ANTES pra o banner não voltar logo após reload se algo
  // falhar. O próximo check periódico (15min) detecta de novo se ainda for
  // o caso. Mas se aplicar OK, current=novo e check vê up_to_date.
  useLiveUpdateStore.getState().setPending(null)
  try {
    await Updater.set({ id: pending.id })
    logInfo(`set() ok — chamando reload() do plugin...`)
    await Updater.reload()
    logInfo(`reload() retornou — WebView deveria estar trocando agora.`)
  } catch (e) {
    logWarn('aplicar bundle falhou', e)
    // Re-marca pending pra usuário poder tentar de novo.
    useLiveUpdateStore.getState().setPending(pending)
  }
}

/**
 * Limpa só o pendingUpdate (UI): banner some até o próximo check periódico
 * detectar o mesmo bundle. O bundle baixado fica armazenado no plugin.
 */
export function discardPendingUpdate(): void {
  useLiveUpdateStore.getState().setPending(null)
  logInfo('pendingUpdate descartado (banner silenciado).')
}

/**
 * Força um check imediato. Usado pelo botão "Verificar agora" da tela admin.
 */
export async function forceCheck(): Promise<void> {
  const Updater = getUpdater()
  if (!Updater) {
    useLiveUpdateStore.getState().finishCheck('error', 'rodando em PWA web — sem plugin')
    return
  }
  await checkAndQueueUpdate(Updater)
}

/**
 * Volta pro bundle embedded do APK. Descarta qualquer OTA. Reinicia a WebView.
 * Usado quando o usuário suspeita de bundle ruim em campo.
 */
export async function resetToNative(): Promise<void> {
  const Updater = getUpdater()
  if (!Updater) return
  logInfo('resetando pro bundle nativo (APK embedded)...')
  try {
    await Updater.reset()
    useLiveUpdateStore.getState().setPending(null)
    setTimeout(() => window.location.reload(), 200)
  } catch (e) {
    logWarn('reset() falhou', e)
  }
}
